// JourneyGoals, CRUD simples de metas/projetos/datas especiais.
// Mobile-first, sem libs externas. Aceita filtro por kind.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Check,
  Loader2,
  Target,
  Briefcase,
  CalendarHeart,
  CircleCheck,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { journeyApi } from "../lib/journeyApi";

const KINDS = [
  { value: "goal", label: "Meta", Icon: Target },
  { value: "project", label: "Projeto", Icon: Briefcase },
  { value: "special_date", label: "Data especial", Icon: CalendarHeart },
];

function kindLabel(k) {
  return (KINDS.find((x) => x.value === k) || KINDS[0]).label;
}

export default function JourneyGoals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    kind: "goal",
    title: "",
    description: "",
    due_at: "",
  });

  const reload = async () => {
    try {
      const r = await journeyApi.listGoals();
      setItems(r);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Dê um título à sua meta.");
      return;
    }
    setCreating(true);
    try {
      const created = await journeyApi.createGoal({
        kind: form.kind,
        title: form.title.trim(),
        description: form.description.trim() || null,
        due_at: form.due_at || null,
      });
      setItems((prev) => [created, ...prev]);
      setForm({ kind: form.kind, title: "", description: "", due_at: "" });
      toast.success("Meta adicionada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar.");
    } finally {
      setCreating(false);
    }
  };

  const toggleDone = async (g) => {
    const next = g.status === "done" ? "active" : "done";
    try {
      const updated = await journeyApi.updateGoal(g.id, { status: next });
      setItems((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Remover "${g.title}"?`)) return;
    try {
      await journeyApi.deleteGoal(g.id);
      setItems((prev) => prev.filter((x) => x.id !== g.id));
      toast.success("Removido.");
    } catch {
      toast.error("Falha ao remover.");
    }
  };

  return (
    <section
      className="bg-white border border-border rounded-2xl p-5 sm:p-6"
      data-testid="journey-goals"
    >
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-gold" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Propósito
        </p>
      </div>
      <h2 className="font-display text-2xl text-navy mt-1">
        Metas, projetos e datas
      </h2>
      <p className="text-sm text-navy/60 mt-2">
        Registros de longo prazo. Marque como concluído quando alcançar.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="journey-goals-form">
        <div className="grid sm:grid-cols-[140px_1fr] gap-3">
          <select
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="journey-goal-kind"
            className="h-11 rounded-md border border-input bg-[hsl(var(--parchment))] px-3 text-sm text-navy focus:bg-white"
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Ex.: Ler Bíblia inteira em 2026"
            maxLength={200}
            data-testid="journey-goal-title"
            className="h-11 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
        </div>
        <Textarea
          rows={2}
          maxLength={2000}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Descrição (opcional)"
          className="bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          data-testid="journey-goal-description"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            type="date"
            value={form.due_at}
            onChange={(e) => setForm({ ...form, due_at: e.target.value })}
            data-testid="journey-goal-due"
            className="h-11 w-44 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
          />
          <button
            type="submit"
            disabled={creating}
            data-testid="journey-goal-submit"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest"
          >
            {creating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            Adicionar
          </button>
        </div>
      </form>

      <ul className="mt-6 space-y-2" data-testid="journey-goals-list">
        {loading ? (
          <li className="text-sm text-navy/55">Carregando…</li>
        ) : items.length === 0 ? (
          <li className="text-sm text-navy/55 italic">
            Nenhuma meta registrada ainda.
          </li>
        ) : (
          items.map((g) => {
            const done = g.status === "done";
            return (
              <li
                key={g.id}
                data-testid={`journey-goal-${g.id}`}
                className={`flex items-start gap-3 p-3 rounded-xl border ${
                  done
                    ? "bg-[hsl(var(--parchment))] border-emerald-200/60"
                    : "bg-white border-border"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleDone(g)}
                  aria-label={done ? "Marcar como ativa" : "Marcar como concluída"}
                  data-testid={`journey-goal-toggle-${g.id}`}
                  className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-colors ${
                    done
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : "border-navy/20 hover:border-gold"
                  }`}
                >
                  {done ? <Check className="w-4 h-4" /> : <CircleCheck className="w-3.5 h-3.5 text-navy/30" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-gold">
                      {kindLabel(g.kind)}
                    </span>
                    {g.due_at && (
                      <span className="text-[10px] text-navy/45">
                        · {g.due_at}
                      </span>
                    )}
                  </div>
                  <p
                    className={`font-display text-base ${
                      done ? "text-navy/55 line-through" : "text-navy"
                    } truncate`}
                  >
                    {g.title}
                  </p>
                  {g.description && (
                    <p className="text-[12px] text-navy/60 mt-0.5 line-clamp-2">
                      {g.description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(g)}
                  data-testid={`journey-goal-delete-${g.id}`}
                  className="p-1.5 rounded-lg hover:bg-rose-50 text-navy/40 hover:text-destructive"
                  aria-label="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })
        )}
      </ul>
    </section>
  );
}
